
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import type { PageFaqGroup } from '@/types/faq'

export type { PageFaqGroup } from '@/types/faq'

let pageFaqInstanceSequence = 0

export interface PageFaqItem {
  id: string;
  question: string;
  answer: string;
}

interface RenderablePageFaqItem {
  readonly id: string;
  readonly question: string;
  readonly answer: string;
  readonly number: number;
  readonly questionId: string;
  readonly buttonId: string;
  readonly panelId: string;
}

interface RenderablePageFaqGroup {
  readonly id: string;
  readonly title: string;
  readonly items: readonly RenderablePageFaqItem[];
}

@Component
export default class PageFaqSection extends Vue {
  @Prop({ type: String, default: '社交分享MGM功能问题解答' })
  readonly title!: string

  @Prop({ type: Array, default: () => [] })
  readonly groups!: readonly PageFaqGroup[]

  renderGroups: readonly RenderablePageFaqGroup[] = []

  activeItemId: string | null = null

  userClosedAll = false

  hasInitialized = false

  private readonly instancePrefix = this.createInstancePrefix()

  readonly titleId = `${this.instancePrefix}-title`

  @Watch('groups', { immediate: true, deep: true })
  onGroupsChanged(value: unknown): void {
    const normalizedGroups = this.normalizeGroups(value)
    const validItemIds = new Set<string>()
    let firstItemId: string | null = null

    normalizedGroups.forEach(group => {
      group.items.forEach(item => {
        if (firstItemId === null) {
          firstItemId = item.id
        }
        validItemIds.add(item.id)
      })
    })

    this.renderGroups = normalizedGroups

    if (!this.hasInitialized) {
      this.activeItemId = firstItemId
      this.hasInitialized = true
    } else if (firstItemId === null) {
      this.activeItemId = null
    } else if (this.activeItemId === null || !validItemIds.has(this.activeItemId)) {
      this.activeItemId = this.userClosedAll ? null : firstItemId
    }

    this.scheduleActivePanelHeightSync()
  }

  updated(): void {
    this.syncActivePanelHeight()
  }

  getGroupTitleId(groupIndex: number): string {
    return `${this.instancePrefix}-group-${groupIndex + 1}`
  }

  isExpanded(itemId: string): boolean {
    return this.activeItemId === itemId
  }

  toggleItem(itemId: string): void {
    if (!this.hasRenderableItem(itemId)) {
      return
    }

    if (this.activeItemId === itemId) {
      this.activeItemId = null
      this.userClosedAll = true
    } else {
      this.activeItemId = itemId
      this.userClosedAll = false
    }

    this.scheduleActivePanelHeightSync()
  }

  onBeforeEnter(element: Element): void {
    const panel = this.getPanelElement(element)
    if (panel === null) {
      return
    }

    panel.style.height = '0'
    panel.style.overflow = 'hidden'
  }

  onEnter(element: Element): void {
    const panel = this.getPanelElement(element)
    if (panel === null) {
      return
    }

    panel.style.height = `${panel.offsetHeight}px`
    this.syncPanelHeight(panel)
  }

  onAfterEnter(element: Element): void {
    const panel = this.getPanelElement(element)
    if (panel === null) {
      return
    }

    this.restorePanelForCurrentState(panel)
  }

  onEnterCancelled(element: Element): void {
    const panel = this.getPanelElement(element)
    if (panel === null) {
      return
    }

    this.restorePanelForCurrentState(panel)
  }

  onBeforeLeave(element: Element): void {
    const panel = this.getPanelElement(element)
    if (panel === null) {
      return
    }

    panel.style.height = `${panel.scrollHeight}px`
    panel.style.overflow = 'hidden'
  }

  onLeave(element: Element): void {
    const panel = this.getPanelElement(element)
    if (panel === null) {
      return
    }

    panel.style.height = `${panel.offsetHeight}px`
    panel.style.height = '0'
  }

  onAfterLeave(element: Element): void {
    const panel = this.getPanelElement(element)
    if (panel === null) {
      return
    }

    this.clearPanelTransitionStyles(panel)
  }

  onLeaveCancelled(element: Element): void {
    const panel = this.getPanelElement(element)
    if (panel === null) {
      return
    }

    this.restorePanelForCurrentState(panel)
  }

  protected scheduleActivePanelHeightSync(): void {
    this.$nextTick(() => {
      this.syncActivePanelHeight()
    })
  }

  protected syncActivePanelHeight(): void {
    if (this.activeItemId === null || !(this.$el instanceof HTMLElement)) {
      return
    }

    const activeItem = this.getRenderableItem(this.activeItemId)
    if (activeItem === null) {
      return
    }

    const panel = this.$el.querySelector(`#${activeItem.panelId}`)
    if (!(panel instanceof HTMLElement)) {
      return
    }

    this.syncPanelHeight(panel)
  }

  private syncPanelHeight(panel: HTMLElement): void {
    if (!this.isActivePanel(panel) || panel.style.height === '' || panel.style.height === 'auto') {
      return
    }

    const targetHeight = `${panel.scrollHeight}px`
    if (panel.style.height !== targetHeight) {
      panel.style.height = targetHeight
    }
  }

  private restorePanelForCurrentState(panel: HTMLElement): void {
    if (this.isActivePanel(panel)) {
      panel.style.height = 'auto'
      panel.style.overflow = 'visible'
      return
    }

    panel.style.height = '0'
    panel.style.overflow = 'hidden'
  }

  private clearPanelTransitionStyles(panel: HTMLElement): void {
    panel.style.removeProperty('height')
    panel.style.removeProperty('overflow')
  }

  private isActivePanel(panel: HTMLElement): boolean {
    if (this.activeItemId === null) {
      return false
    }

    const activeItem = this.getRenderableItem(this.activeItemId)
    return activeItem !== null && activeItem.panelId === panel.id
  }

  private getPanelElement(element: Element): HTMLElement | null {
    return element instanceof HTMLElement ? element : null
  }

  private hasRenderableItem(itemId: string): boolean {
    return this.getRenderableItem(itemId) !== null
  }

  private getRenderableItem(itemId: string): RenderablePageFaqItem | null {
    for (const group of this.renderGroups) {
      const item = group.items.find(candidate => candidate.id === itemId)
      if (item !== undefined) {
        return item
      }
    }

    return null
  }

  protected normalizeGroups(value: unknown): readonly RenderablePageFaqGroup[] {
    if (!Array.isArray(value)) {
      this.warnIgnoredRecord('FAQ data', 'groups', 'expected an array')
      return []
    }

    const seenGroupIds = new Set<string>()
    const seenItemIds = new Set<string>()
    const normalizedGroups: RenderablePageFaqGroup[] = []
    let globalNumber = 0

    value.forEach((groupValue: unknown, groupIndex: number) => {
      const groupPath = `groups[${groupIndex}]`
      if (!this.isRecord(groupValue)) {
        this.warnIgnoredRecord('group', groupPath, 'expected a non-null object')
        return
      }

      const groupId = this.normalizeId(groupValue.id)
      if (groupId === null) {
        this.warnIgnoredRecord('group', groupPath, 'id must be a non-empty string')
        return
      }

      if (typeof groupValue.title !== 'string' || groupValue.title.trim() === '') {
        this.warnIgnoredRecord('group', groupPath, 'title must be a non-empty string', groupId)
        return
      }

      if (!Array.isArray(groupValue.items)) {
        this.warnIgnoredRecord('group', groupPath, 'items must be an array', groupId)
        return
      }

      if (seenGroupIds.has(groupId)) {
        this.warnIgnoredRecord('group', groupPath, 'duplicate group id', groupId)
        return
      }
      seenGroupIds.add(groupId)

      const normalizedItems: RenderablePageFaqItem[] = []
      groupValue.items.forEach((itemValue: unknown, itemIndex: number) => {
        const itemPath = `${groupPath}.items[${itemIndex}]`
        if (!this.isRecord(itemValue)) {
          this.warnIgnoredRecord('item', itemPath, 'expected a non-null object')
          return
        }

        const itemId = this.normalizeId(itemValue.id)
        if (itemId === null) {
          this.warnIgnoredRecord('item', itemPath, 'id must be a non-empty string')
          return
        }

        if (typeof itemValue.question !== 'string') {
          this.warnIgnoredRecord('item', itemPath, 'question must be a string', itemId)
          return
        }

        if (typeof itemValue.answer !== 'string') {
          this.warnIgnoredRecord('item', itemPath, 'answer must be a string', itemId)
          return
        }

        if (seenItemIds.has(itemId)) {
          this.warnIgnoredRecord('item', itemPath, 'duplicate item id', itemId)
          return
        }
        seenItemIds.add(itemId)

        globalNumber += 1
        normalizedItems.push({
          id: itemId,
          question: itemValue.question,
          answer: itemValue.answer,
          number: globalNumber,
          questionId: this.getQuestionId(globalNumber),
          buttonId: this.getButtonId(globalNumber),
          panelId: this.getPanelId(globalNumber),
        })
      })

      normalizedGroups.push({
        id: groupId,
        title: groupValue.title,
        items: normalizedItems,
      })
    })

    return normalizedGroups
  }

  protected getQuestionId(number: number): string {
    return `${this.instancePrefix}-question-${number}`
  }

  protected getButtonId(number: number): string {
    return `${this.instancePrefix}-button-${number}`
  }

  protected getPanelId(number: number): string {
    return `${this.instancePrefix}-panel-${number}`
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
  }

  private normalizeId(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null
    }

    const normalizedId = value.trim()
    return normalizedId === '' ? null : normalizedId
  }

  private warnIgnoredRecord(recordType: string, path: string, reason: string, id?: string): void {
    if (process.env.NODE_ENV === 'production') {
      return
    }

    const idContext = id === undefined ? '' : ` with id ${JSON.stringify(id)}`
    console.warn(`[PageFaqSection] Ignored ${recordType} at ${path}${idContext}: ${reason}.`)
  }

  private createInstancePrefix(): string {
    pageFaqInstanceSequence += 1
    return `page-faq-${pageFaqInstanceSequence}`
  }
}
